/**
 * Wartości widgetów w podglądzie „na żywo" (bez sieci) — liczone po stronie
 * klienta z syntetycznych wierszy, zamiast `GET …/stats` (backend liczy je na
 * realnych danych tenanta, których w Kreatorze nie ma).
 *
 * Parytet z I1 endpointu stats: `counter` (liczba wierszy wg filtra) +
 * `aggregate` (sum/avg/min/max/count/count_distinct na polu). Pozostałe typy
 * widgetów → null (pasek pokazuje „—", tak samo jak realny pasek przed I2+).
 *
 * Filtry widgetu honorujemy w zakresie eq/neq/in/is_null/is_not_null — inne
 * operatory PRZEPUSZCZAJĄ wiersz. Wartości są poglądowe (5 syntetycznych
 * wierszy); lepiej pokazać liczbę niż wyzerować kartę przez nieobsłużony
 * operator.
 */

import type { FilterConfig, ModuleTemplateFieldDefinition, WidgetConfig } from '@saas/shared';

export type SampleRow = Record<string, unknown>;

/** Czy wiersz przechodzi pojedynczy warunek filtra (nieobsłużony operator → tak). */
export function rowMatchesFilter(row: SampleRow, filter: FilterConfig): boolean {
  const raw = row[filter.field];
  switch (filter.operator) {
    case 'eq':
      return String(raw ?? '') === String(filter.value ?? '');
    case 'neq':
      return String(raw ?? '') !== String(filter.value ?? '');
    case 'in':
      return Array.isArray(filter.value)
        ? filter.value.map(String).includes(String(raw ?? ''))
        : true;
    case 'is_null':
      return raw == null || raw === '';
    case 'is_not_null':
      return raw != null && raw !== '';
    default:
      return true;
  }
}

/**
 * Czy wiersz przechodzi listę warunków (AND/OR). Reużywane też przez podgląd
 * szybkich filtrów pozycji menu (chipy = `quickFilter.filters`, logika AND).
 */
export function rowMatchesFilters(
  row: SampleRow,
  filters: FilterConfig[],
  logic: 'and' | 'or' = 'and',
): boolean {
  if (filters.length === 0) return true;
  return logic === 'or'
    ? filters.some((f) => rowMatchesFilter(row, f))
    : filters.every((f) => rowMatchesFilter(row, f));
}

/** Wiersze przechodzące filtry widgetu (filter + filters, logika and/or). */
function matchingRows(widget: WidgetConfig, rows: SampleRow[]): SampleRow[] {
  const filters: FilterConfig[] = [
    ...(widget.filter ? [widget.filter] : []),
    ...(widget.filters ?? []),
  ];
  return rows.filter((row) => rowMatchesFilters(row, filters, widget.filterLogic ?? 'and'));
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Wartość jednego widgetu z próbek; null = typ/agregacja poza parytetem I1. */
export function computePreviewWidgetValue(widget: WidgetConfig, rows: SampleRow[]): number | null {
  const type = widget.type ?? 'counter';
  const matched = matchingRows(widget, rows);

  if (type === 'counter') return matched.length;

  if (type === 'aggregate') {
    const fn = widget.aggregateFunction ?? 'count';
    if (fn === 'count') return matched.length;
    const field = widget.aggregateField;
    if (!field) return null;
    if (fn === 'count_distinct') {
      return new Set(matched.map((r) => String(r[field] ?? ''))).size;
    }
    const nums = matched.map((r) => toNumber(r[field])).filter((n): n is number => n !== null);
    if (nums.length === 0) return null;
    switch (fn) {
      case 'sum':
        return nums.reduce((a, b) => a + b, 0);
      case 'avg':
        return nums.reduce((a, b) => a + b, 0) / nums.length;
      case 'min':
        return Math.min(...nums);
      case 'max':
        return Math.max(...nums);
      default:
        return null; // median/stddev/variance — poza parytetem I1, jak backend
    }
  }

  return null; // wykresy/trendy itd. — pasek pokaże „—"
}

/** Mapa `widget.id → wartość` do wstrzyknięcia w `ListWidgetsBar` (staticValues). */
export function computePreviewWidgetValues(
  widgets: WidgetConfig[],
  rows: SampleRow[],
): Record<string, number | null> {
  return Object.fromEntries(widgets.map((w) => [w.id, computePreviewWidgetValue(w, rows)]));
}

/** Etykiety widgetów demonstracyjnych — tłumaczone po stronie wołającego (t()). */
export interface DemoWidgetLabels {
  total: string;
  sum: (fieldName: string) => string;
}

/**
 * Widgety demonstracyjne podglądu — gdy widok listy nie ma jeszcze żadnych
 * widgetów, pasek pokazuje przykładowe zamiast znikać (widgety definiuje się
 * w Kreatorze Listy, więc podgląd zawsze prezentuje tę sekcję). Dobór z pól
 * encji: licznik wszystkich wierszy; licznik filtrowany pierwszą opcją
 * pierwszego pola wyboru; suma pierwszego pola liczbowego.
 */
export function buildDemoPreviewWidgets(
  fields: ModuleTemplateFieldDefinition[],
  labels: DemoWidgetLabels,
): WidgetConfig[] {
  const widgets: WidgetConfig[] = [
    {
      id: 'preview-demo-total',
      label: labels.total,
      color: '#2563eb',
      icon: 'list',
      type: 'counter',
      clickAction: 'none',
      order: 0,
    },
  ];
  const select = fields.find((f) => f.type === 'select' && (f.options?.length ?? 0) > 0);
  if (select) {
    const option = select.options![0]!;
    widgets.push({
      id: 'preview-demo-select',
      label: `${select.name}: ${option.label}`,
      color: '#16a34a',
      icon: 'filter',
      type: 'counter',
      clickAction: 'none',
      order: 1,
      filter: { field: select.slug, operator: 'eq', value: option.value },
    });
  }
  const numeric = fields.find((f) => f.type === 'number' || f.type === 'decimal');
  if (numeric) {
    widgets.push({
      id: 'preview-demo-sum',
      label: labels.sum(numeric.name),
      color: '#9333ea',
      icon: 'sigma',
      type: 'aggregate',
      clickAction: 'none',
      order: 2,
      aggregateFunction: 'sum',
      aggregateField: numeric.slug,
    });
  }
  return widgets;
}
